import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { UseFormReturn } from "react-hook-form";
import { SegmentedTabs } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { summarizeItem } from "@/lib/prescriptions/editorDraft";
import type {
  ControlRegime,
  MedicalPrescriptionInput,
  PrescriptionItemInput,
} from "@/lib/validations/medical-prescription";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";
import { AddItemTrigger } from "./AddItemTrigger";
import { ItemCard } from "./ItemCard";
import { MedicationItemEditor } from "./MedicationItemEditor";
import { PrescriptionMetaFields } from "./PrescriptionMetaFields";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

export type EditorKind = "standard" | "controlled" | "manipulated" | "exam";
export type ExpandedState = { mode: "new" } | { mode: "edit"; index: number };

export function PrescriptionTypeSelector({
  kind,
  onChange,
}: {
  kind: EditorKind;
  onChange: (kind: EditorKind) => void;
  mobile?: boolean;
}) {
  const { t } = useTranslation("prescriptions");
  return (
    <SegmentedTabs
      value={kind}
      onValueChange={(value) => onChange(value as EditorKind)}
      variant="outline-secondary"
      scrollable
      label={t("composer.prescriptionType")}
      items={[
        { value: "standard", label: t("editor.kinds.standard") },
        { value: "controlled", label: t("editor.kinds.controlled") },
        { value: "manipulated", label: t("editor.kinds.manipulated") },
        { value: "exam", label: t("editor.kinds.exam") },
      ]}
    />
  );
}

export interface MedicationListEditorProps {
  form: UseFormReturn<MedicalPrescriptionInput>;
  kind: "standard" | "controlled";
  clinicId: string;
  speciesId?: string;
  mobile?: boolean;
  items: PrescriptionItemInput[];
  expanded: ExpandedState | null;
  suggestedRegime: ControlRegime | null;
  templateSlot: ReactNode;
  footerSlot?: ReactNode;
  onOpenNew: () => void;
  onOpenEdit: (index: number) => void;
  onCloseExpanded: () => void;
  onCommit: (item: PrescriptionItemInput) => void;
  onRemove: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMove: (from: number, to: number) => void;
  onDraftChange: (item: PrescriptionItemInput | null) => void;
  onOpenCreateMedication: (name: string, onCreated: (medication: MedicationRow) => void) => void;
}

/** Items as cards; "Adicionar medicamento" and "Editar" expand in place of the button/card. */
export function MedicationListEditor(props: MedicationListEditorProps) {
  const { t, i18n } = useTranslation("prescriptions");
  const lang = i18n.language?.toLowerCase().startsWith("pt") ? "pt" : "en";
  const { form, items, expanded, mobile = false } = props;
  const itemsError = (form.formState.errors.items as { message?: string; root?: { message?: string } } | undefined);
  const itemsMessage = itemsError?.message ?? itemsError?.root?.message;

  const summaries = useMemo(() => items.map((item) => summarizeItem(item, lang)), [items, lang]);

  return (
    <div className="flex flex-col gap-5">
      {props.templateSlot}

      <div className="flex flex-col gap-2.5">
        {items.map((item, index) =>
          expanded?.mode === "edit" && expanded.index === index ? (
            <MedicationItemEditor
              key={`edit-${index}`}
              initial={item}
              ordinal={index + 1}
              clinicId={props.clinicId}
              speciesId={props.speciesId}
              mobile={mobile}
              onCommit={props.onCommit}
              onCancel={props.onCloseExpanded}
              onDraftChange={props.onDraftChange}
              onOpenCreateMedication={props.onOpenCreateMedication}
            />
          ) : (
            <ItemCard
              key={`item-${index}-${item.medication_name}`}
              index={index}
              title={summaries[index].title || t("composer.medication")}
              summary={summaries[index].summary}
                            mobile={mobile}
              onEdit={() => props.onOpenEdit(index)}
              onDuplicate={() => props.onDuplicate(index)}
              onRemove={() => props.onRemove(index)}
              onMoveUp={index > 0 ? () => props.onMove(index, index - 1) : undefined}
              onMoveDown={index < items.length - 1 ? () => props.onMove(index, index + 1) : undefined}
            />
          ),
        )}

        {expanded?.mode === "new" ? (
          <MedicationItemEditor
            key="new-item"
            initial={null}
            ordinal={items.length + 1}
            clinicId={props.clinicId}
            speciesId={props.speciesId}
            mobile={mobile}
            onCommit={props.onCommit}
            onCancel={props.onCloseExpanded}
            onDraftChange={props.onDraftChange}
            onOpenCreateMedication={props.onOpenCreateMedication}
          />
        ) : (
          <AddItemTrigger label={t("composer.addMedication")} mobile={mobile} onClick={props.onOpenNew} />
        )}
        {itemsMessage ? <p className="text-sm text-destructive" role="alert">{itemsMessage}</p> : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="prescription-observations" className="text-[12.5px] font-semibold text-muted-foreground">
          {t("examComposer.complementaryGuidance")}
        </Label>
        <Textarea
          id="prescription-observations"
          rows={2}
          className={cn("rounded-xl", mobile && "text-base")}
          placeholder={t("examComposer.complementaryGuidancePlaceholder")}
          {...form.register("observations")}
        />
      </div>

      <PrescriptionMetaFields form={form} kind={props.kind} suggestedRegime={props.suggestedRegime} mobile={mobile} />
      {props.footerSlot}
    </div>
  );
}
